/**
 * Impressum and privacy statement of the platform operator.
 *
 * IMPRESSUM: copied verbatim from ~/Developer/AppStore-Apps/site (section
 * #impressum), which the owner confirmed as current on 11 September 2026. It
 * is never written or "improved" here — change it there first, then copy.
 *
 * PRIVACY: describes only what this app actually does (docs/spec.md 15,
 * CLAUDE.md, infra/*.bicep). Every deletion period comes from RETENTION in
 * @fsv/core, which db/retention.sql enforces and db/tests/retention.test.ts
 * checks — the text cannot promise a period the weekly job does not apply.
 *
 * Push notifications (0010, 4 October 2026): what the device token is, who
 * transmits (Apple, Google), what a message contains, how long the token is
 * kept (RETENTION.pushDeviceDays, db/retention.sql) and how to switch off —
 * texts.test.ts ties each statement to the code that makes it true.
 *
 * Sign-in through Vereinsflieger (6 October 2026): removed, and with it the
 * paragraph on passing credentials to vereinsflieger.de (ADR 0002). Member
 * data may come from a club's Vereinsflieger export, uploaded by the club.
 *
 * STATUS: proposal of 3 October 2026, not reviewed by a lawyer. The role split
 * (club = controller, operator = processor under Art. 28 GDPR) needs a data
 * processing agreement with every club before real use beyond the FSV.
 */
import { OPERATOR, RETENTION, TERMS_VERSION, SUPPORT_EMAIL, VIDEO_MAX_SECONDS, months, PRIVACY_URL, TERMS_URL, type ContractText, type LegalSection } from '@fsv/core';

// The contract texts (AGB, AVV) and the operator's data moved to @fsv/core
// (legal.ts) with the online sign-up (0024): the API mails them in full.
export {
  AGB,
  AGB_URL,
  AVV,
  AVV_URL,
  CONTRACT_END,
  OPERATOR,
  SIGNUP_URL,
  SUPPORT_EMAIL,
  contractPlainText,
  type ContractText,
  type LegalSection,
} from '@fsv/core';

export const LEGAL_VERSION = '29.10.2026';

/** Verbatim from the operator's confirmed Impressum (see header). */
export const IMPRESSUM = {
  title: 'Impressum',
  basis: 'Angaben gemäß § 5 DDG:',
  rows: [
    { term: 'Vertretungsberechtigter', text: OPERATOR.representative },
    { term: 'Handelsregister', text: OPERATOR.register },
    { term: 'Umsatzsteuer-ID', text: OPERATOR.vatId },
    { term: 'Telefon', text: OPERATOR.phone },
    { term: 'Telefax', text: OPERATOR.fax },
    { term: 'E-Mail', text: OPERATOR.email },
  ],
  disputeTitle: 'Streitbeilegung',
  dispute:
    'Wir sind nicht bereit oder verpflichtet, an Streitbeilegungsverfahren vor einer Verbraucherschlichtungsstelle teilzunehmen.',
  footer: 'Außencheck ist eine App der D-VO Consulting UG (haftungsbeschränkt).',
} as const;

export const PRIVACY: { title: string; intro: string; sections: readonly LegalSection[] } = {
  title: 'Datenschutzerklärung',
  intro:
    'Außencheck dokumentiert Lackschäden, Kratzer, Beulen und ähnliche Auffälligkeiten an den Flugzeugen der teilnehmenden Vereine. Die App ist eine geschlossene Anwendung: Zugang erhält nur, wen ein Verein als Mitglied eingetragen hat. Einzelpersonen können sich nicht selbst registrieren – nur ein Verein schließt online einen Vertrag. Es gibt keine Werbung, keine Analyse des Nutzungsverhaltens und keine Cookies.',
  sections: [
    {
      id: 'verantwortlich',
      title: 'Wer verantwortlich ist',
      paragraphs: [
        'Für die Daten innerhalb eines Vereins – Mitgliederliste, Schadensmeldungen, Fotos, Rückfragen/Hinweise, Hinweise der Technik und das Änderungsprotokoll – ist der jeweilige Verein verantwortlich. Seine Kontaktangaben stehen auf der Datenschutzseite des Vereins in der App.',
        `Die ${OPERATOR.name} betreibt die Plattform und verarbeitet diese Daten im Auftrag des Vereins (Art. 28 DSGVO). Für den Betrieb der Plattform selbst – Anmeldung, Sitzungen, Anmeldeprotokoll und technische Protokolle – ist sie selbst verantwortlich.`,
        `Kontakt: ${OPERATOR.name}, ${OPERATOR.street}, ${OPERATOR.city}, vertreten durch ${OPERATOR.representative}, E-Mail ${OPERATOR.email}. Vollständige Angaben im Impressum.`,
      ],
    },
    {
      id: 'daten',
      title: 'Welche Daten verarbeitet werden',
      items: [
        {
          term: 'Mitgliedsdaten',
          text: 'E-Mail-Adresse, Name sowie Rolle und Status im Verein (Mitglied, Technik, Administration). Eingetragen vom Verein, nicht von Ihnen selbst – von Hand oder als Datei, etwa aus der Mitgliederliste des Vereins in Vereinsflieger. Mit vereinsflieger.de selbst tauscht die App keine Daten aus. Lädt der Verein Sie ein, bekommen Sie eine Einladung per E-Mail; gespeichert wird, ob und wann sie verschickt wurde und wer eingeladen hat.',
        },
        {
          term: 'Weitere E-Mail-Adressen',
          text: 'Mit weiteren E-Mail-Adressen können Sie sich ebenfalls anmelden. Die Verwaltung Ihres Vereins kann sie eintragen; Sie selbst fügen eine im Profil hinzu, nachdem Sie einen Bestätigungscode an diese Adresse erhalten und eingegeben haben – vom Code wird nur eine Prüfsumme gespeichert. Gespeichert werden die Adresse, wer sie eingetragen hat und wann, und bei einer entfernten Adresse, wer sie wann entfernt hat. Die Verwaltung Ihrer Vereine sieht Ihre weiteren Adressen in der Mitgliederliste. Einladungen und andere Nachrichten gehen weiter an die erste Adresse. Eine entfernte Adresse bleibt als entfernt in der Änderungshistorie, bis Ihre Person gelöscht wird.',
        },
        {
          term: 'Anmeldung',
          text: 'Bei der Anmeldung per E-Mail erhalten Sie einen sechsstelligen Einmalcode; gespeichert wird nur eine nicht umkehrbare Prüfsumme, der Code verfällt nach 15 Minuten. Nach der Anmeldung liegt ein zufälliges Sitzungstoken im Speicher Ihres Browsers; auf dem Server liegt davon nur eine Prüfsumme.',
        },
        {
          term: 'Schadensmeldungen',
          text: 'Was Sie erfassen: Flugzeug, Position auf der Zeichnung, Kategorie, Titel, Beschreibung, Status und Rückfragen/Hinweise – jeweils mit Ihrem Namen als Meldende oder Meldender und dem Zeitpunkt.',
        },
        {
          term: 'Hinweise der Technik',
          text: 'Technik und Administration tragen zu einem Flugzeug Hinweise ein (etwa ein ausgefallenes Gerät) – mit Namen und Zeitpunkt. Bestätigen Sie einen Hinweis mit „Gelesen“, speichern wir das mit Ihrer Person und dem Zeitpunkt; Technik und Administration Ihres Vereins sehen, wer einen Hinweis wann gelesen hat. Aufgehobene Hinweise bleiben als Verlauf erhalten.',
        },
        {
          term: 'Videos',
          text: `Ein Video zu einem Schaden wird auf Ihrem Gerät verkleinert (höchstens ${VIDEO_MAX_SECONDS} Sekunden, 720p). Dabei werden alle Metadaten entfernt, auch der Aufnahmeort; der Server nimmt kein Video an, das solche Daten noch enthält. Der Ton bleibt erhalten – sprechen Sie beim Filmen, ist das im Video zu hören. Für Videos gelten dieselben Lösch- und Aufbewahrungsregeln wie für Fotos.`,
        },
        {
          term: 'Fotos',
          text: `Fotos werden auf Ihrem Gerät verkleinert. Dabei werden Ortsangaben und alle übrigen Metadaten (EXIF) entfernt; der Server nimmt kein Foto an, das solche Daten noch enthält. Hochgeladene Fotos, die nie einer Meldung zugeordnet wurden (etwa weil die Verbindung abbrach), werden nach ${RETENTION.orphanPhotoHours} Stunden entfernt.`,
        },
        {
          term: 'Profilbild',
          text: `Freiwillig können Sie ein Profilbild hinterlegen. Es wird auf Ihrem Gerät quadratisch zugeschnitten, auf 256 × 256 Pixel verkleinert und ohne Metadaten (EXIF) hochgeladen; der Server nimmt kein Bild an, das solche Daten noch enthält. Sichtbar ist es neben Ihrem Namen für die Mitglieder der Vereine, in denen Sie Mitglied sind. Ersetzen oder entfernen Sie es, wird das alte Bild gelöscht; ebenso beim Anonymisieren Ihres Kontos. Die Datei ist dann nicht mehr abrufbar; technisch liegt sie noch bis zu ${RETENTION.photoSoftDeleteDays} Tage im Speicher und wird danach endgültig entfernt.`,
        },
        {
          term: 'Benachrichtigungen (nur App)',
          text: `Erlauben Sie der App für iOS oder Android Benachrichtigungen, speichern wir das Geräte-Token, das Apple bzw. Google dafür vergibt, zusammen mit Ihrer Person, dem Gerätetyp (iOS oder Android) und der App-Version. Technik und Administration erhalten eine Nachricht über jede neue Schadensmeldung im Verein und jede neue Rückfrage/jeden neuen Hinweis dazu; wer einen Schaden gemeldet hat, erhält eine über Statusänderungen und Rückfragen/Hinweise zu dieser Meldung. Eine Nachricht enthält das Kennzeichen, den Titel des Schadens bzw. den neuen Status oder die Begründung einer Verwerfung – keine Namen, keine E-Mail-Adressen und nicht den Text einer Rückfrage/eines Hinweises. Abschalten lässt sich das je Verein im Profil der App oder ganz in den Systemeinstellungen des Geräts; beim Abmelden wird das Gerät gelöscht, beim Abmelden auf allen Geräten jedes Ihrer Geräte.`,
        },
        {
          term: 'Vereinsanmeldung und Vertrag',
          text: 'Schließt ein Verein den Vertrag online ab, speichern wir die Angaben aus dem Formular: Name und Anschrift des Vereins, Vereinsregister, Name und Funktion der vertretungsberechtigten Person, ihre E-Mail-Adresse und freiwillig Telefonnummer, Rechnungsanschrift, USt-IdNr., Zahlweise und die angegebene Flotte, dazu den Zeitpunkt, die Fassungen von AGB und Auftragsverarbeitungsvertrag und eine nicht umkehrbare Prüfsumme der IP-Adresse. An die E-Mail-Adresse geht ein Bestätigungscode, von dem nur eine Prüfsumme gespeichert wird. Mit dem Code wird der Vertrag geschlossen, der Vereinsbereich eingerichtet und die Person als Verwaltung des Vereins eingetragen; Vertragsbestätigung und eine Benachrichtigung der Betreiberin gehen per E-Mail. Für diese Daten ist die Betreiberin selbst verantwortlich (Art. 6 Abs. 1 lit. b DSGVO).',
        },
        {
          term: 'Änderungsprotokoll',
          text: 'Jede Änderung an einem Eintrag wird mit Zeitpunkt, handelnder Person sowie altem und neuem Stand festgehalten. Das dient der Nachvollziehbarkeit gegenüber Technik, Werft und Versicherung.',
        },
        {
          term: 'Anmeldeprotokoll',
          text: 'Zeitpunkt, Ergebnis und Anmeldeweg jeder Anmeldung. E-Mail- und IP-Adresse werden dabei nur als nicht umkehrbare Prüfsumme gespeichert.',
        },
        {
          term: 'Technische Protokolle',
          text: 'Die Serverkomponente schreibt Betriebsprotokolle (Zeitpunkt, aufgerufene Adresse, Antwortzeit, Fehlermeldungen) zur Fehlersuche, ohne IP-Adressen. Fotos lädt Ihr Browser direkt aus dem Dateispeicher; diese Zugriffe protokolliert der Speicher mit Zeitpunkt, Dateiname und Ihrer IP-Adresse, um Missbrauch erkennen zu können.',
        },
      ],
    },
    {
      id: 'zwecke',
      title: 'Zwecke und Rechtsgrundlagen',
      paragraphs: [
        'Zweck ist die gemeinsame Dokumentation von Schäden an den Vereinsflugzeugen, damit bekannte Schäden nicht mehrfach gemeldet werden und ihr Verlauf nachvollziehbar bleibt.',
        'Rechtsgrundlage ist Art. 6 Abs. 1 lit. b DSGVO (Mitgliedschaft im Verein und Nutzung der App) sowie lit. f (berechtigtes Interesse an einem sicheren Betrieb und an einer nachvollziehbaren Dokumentation sicherheitsrelevanter Schäden).',
      ],
    },
    {
      id: 'empfaenger',
      title: 'Empfänger und Auftragsverarbeiter',
      list: [
        'Ihr Verein: Mitglieder sehen die Schäden ihres Vereins; Technik und Administration zusätzlich Protokoll und Mitgliederliste. Andere Vereine sehen nichts davon.',
        'Microsoft (Microsoft Ireland Operations Ltd.) als Auftragsverarbeiter für Hosting, Datenbank, Dateispeicher, E-Mail-Versand der Anmeldecodes und Einladungen und Betriebsprotokolle (Microsoft Azure).',
        'Apple (Apple Push Notification service) bzw. Google (Firebase Cloud Messaging), nur wenn Sie in der App Benachrichtigungen erlauben: Sie erhalten das Geräte-Token und den Inhalt der Benachrichtigung, um sie an Ihr Gerät zuzustellen. Dabei können Daten in die USA übermittelt werden.',
        `Ihr Verein – und bei Vertragsende ersatzweise die Betreiberin – kann eine vollständige Kopie der Vereinsdaten erstellen (Art. 20 DSGVO), etwa um sie mitzunehmen. Jeder solche Export wird mit Person und Zeitpunkt im Änderungsprotokoll des Vereins vermerkt; die Datei wird nach ${RETENTION.exportDays} Tagen automatisch gelöscht, der Download-Link gilt eine Stunde.`,
        'Eine Weitergabe oder ein Verkauf an Dritte zu Werbezwecken findet nicht statt.',
      ],
    },
    {
      id: 'orte',
      title: 'Wo die Daten liegen',
      paragraphs: [
        'Die Datenbank liegt in Microsoft Azure in Frankfurt am Main (Region Germany West Central). Fotos, Programmlogik und Betriebsprotokolle liegen in der Region Westeuropa (Niederlande); Anmeldecodes und Einladungen werden über Azure Communication Services mit Datenstandort Europa versandt.',
        'Die Web-Oberfläche selbst (Programmdateien ohne Ihre Daten) wird über Azure Static Web Apps ausgeliefert; dabei verarbeitet Microsoft technisch bedingt Ihre IP-Adresse. Schriften und alle Inhalte kommen vom eigenen Server; es werden keine Dienste Dritter geladen.',
      ],
    },
    {
      id: 'dauer',
      title: 'Speicherdauer',
      list: [
        'Mitgliedsdaten, Schadensmeldungen, Fotos, Rückfragen/Hinweise und Hinweise der Technik samt Lesebestätigungen: solange der Verein sie führt. Im Verein gelöschte Einträge bleiben wiederherstellbar, bis der Verein ihre endgültige Entfernung veranlasst.',
        `Änderungsprotokoll: ${months(RETENTION.changeEventsMonths)}.`,
        `Anmeldeprotokoll: ${months(RETENTION.loginEventsMonths)}.`,
        'Anmeldecodes: 15 Minuten; abgelaufene Sitzungen werden wöchentlich entfernt.',
        // Owner (15.10.2026): state the storage fact, never suggest that anybody may ask for a file back.
        `Entfernte Fotos: nicht mehr abrufbar; technisch noch bis zu ${RETENTION.photoSoftDeleteDays} Tage im Speicher, danach endgültig gelöscht.`,
        `Nie zugeordnete Fotos: ${RETENTION.orphanPhotoHours} Stunden, danach ebenso noch bis zu ${RETENTION.photoSoftDeleteDays} Tage im Speicher.`,
        `Ersetzte oder entfernte Profilbilder: nicht mehr abrufbar; technisch noch bis zu ${RETENTION.photoSoftDeleteDays} Tage im Speicher, danach endgültig gelöscht.`,
        `Vereinsexporte: ${RETENTION.exportDays} Tage.`,
        `Nicht bestätigte Vereinsanmeldungen: ${RETENTION.unconfirmedSignupDays} Tage, dann mit ihrer Historie gelöscht. Geschlossene Verträge bewahrt die Betreiberin als Geschäftsunterlagen auf.`,
        `Geräte-Token für Benachrichtigungen: bis zur Abmeldung in der App oder bis Apple bzw. Google das Token als ungültig melden, spätestens ${RETENTION.pushDeviceDays} Tage nachdem sich die App zuletzt gemeldet hat.`,
        'Nach einer Löschung Ihrer Person (Anonymisierung) werden Name und E-Mail-Adressen durch eine Kennung ersetzt und Ihre Geräte-Token für Benachrichtigungen gelöscht; Ihre Schadensmeldungen bleiben dem Verein erhalten. Frühere Fassungen in der Änderungshistorie werden spätestens mit dem nächsten wöchentlichen Lauf bereinigt, Sicherungskopien mit ihrem Ablauf.',
        `Betriebsprotokolle: ${RETENTION.operationalLogDays} Tage.`,
        `Sicherungskopien: Wiederherstellung auf jeden Zeitpunkt der letzten ${RETENTION.pointInTimeRestoreDays} Tage, wöchentliche Sicherungen ${months(RETENTION.longTermBackupMonths)}, wöchentliche Exporte ${RETENTION.dumpDays} Tage. Daten, die gelöscht wurden, können bis zum Ablauf dieser Fristen noch in Sicherungen enthalten sein.`,
      ],
    },
    {
      id: 'nicht',
      title: 'Was die App nicht tut',
      list: [
        'Kein Standortzugriff, kein Adressbuch, kein Zugriff auf Fotos über das hinaus, was Sie selbst auswählen oder aufnehmen.',
        'Keine Cookies, keine Werbe- oder Analysebibliotheken, kein Tracking.',
        'Im Browser werden nur das Sitzungstoken, der zuletzt geöffnete Verein und – im Demo-Modus – die Demodaten gespeichert.',
        'In der App für iOS und Android liegt das Sitzungstoken im geschützten Schlüsselspeicher des Geräts (Schlüsselbund bzw. Keystore). Damit Flotte und Schäden auch ohne Netz lesbar sind, behält die App eine Kopie der zuletzt geladenen Vereinsdaten, höchstens sieben Tage seit dem letzten Abruf; Abmelden löscht sie. Eine ohne Verbindung erfasste Meldung bleibt mit ihren verkleinerten Fotos auf dem Gerät, bis sie übertragen ist.',
      ],
    },
    {
      id: 'demo',
      title: 'Demo-Modus',
      paragraphs: [
        'Die Demo unter /demo arbeitet ausschließlich mit erfundenen Daten im Speicher Ihres Browsers. Es wird nichts an den Server übertragen; „Demo zurücksetzen" oder das Löschen der Website-Daten entfernt alles.',
        'In der App startet „Demo ansehen" dieselbe Demo; die erfundenen Daten und Fotos, die Sie dort aufnehmen, bleiben auf dem Gerät und werden nicht an den Server übertragen.',
      ],
    },
    {
      id: 'rechte',
      title: 'Ihre Rechte',
      paragraphs: [
        `Sie haben das Recht auf Auskunft, Berichtigung, Löschung, Einschränkung der Verarbeitung, Datenübertragbarkeit und Widerspruch. Wenden Sie sich an Ihren Verein oder formlos an ${OPERATOR.email}; wir leiten Anliegen, die Vereinsdaten betreffen, an den Verein weiter.`,
        'Sie können sich außerdem bei einer Aufsichtsbehörde beschweren, etwa beim Landesbeauftragten für den Datenschutz und die Informationsfreiheit Baden-Württemberg.',
      ],
    },
  ],
};

/**
 * Terms of use for the people a club registers (Nutzungsbedingungen); the
 * club's own contract texts (AGB, AVV) are in @fsv/core (legal.ts). Drafted
 * 29.10.2026, not reviewed by a lawyer.
 */
export const TERMS: ContractText = {
  title: 'Nutzungsbedingungen',
  version: TERMS_VERSION,
  intro:
    'Diese Bedingungen gelten für alle Personen, die Außencheck als Mitglied eines Vereins nutzen – im Browser oder in der App. Für Sie ist die Nutzung kostenlos, ohne Werbung und ohne In-App-Käufe.',
  sections: [
    {
      id: 'zugang',
      title: 'Wer Außencheck nutzen darf',
      paragraphs: [
        'Außencheck nutzen kann nur, wen ein Verein, der Außencheck einsetzt, als Mitglied eingetragen hat. Eine öffentliche Registrierung gibt es nicht.',
        'Ihr Zugang zu einem Verein endet, wenn der Verein Ihre Mitgliedschaft in Außencheck beendet – in der Regel mit dem Ende Ihrer Mitgliedschaft im Verein – oder wenn der Vertrag des Vereins über Außencheck endet. Sind Sie in mehreren Vereinen eingetragen, gilt das für jeden Verein einzeln.',
      ],
    },
    {
      id: 'was',
      title: 'Was Außencheck ist',
      paragraphs: [
        'Außencheck ist das Schadensregister Ihres Vereins: Mitglieder melden Schäden an den Vereinsflugzeugen mit Foto und Position auf der Zeichnung, die Technik sichtet sie und dokumentiert jeden Schaden bis zur Reparatur.',
        'Ihr Verein stellt Ihnen Außencheck zur Verfügung. Er entscheidet, wer es nutzt, welche Rolle jemand hat und wie Mitglieder mitwirken – ob sie selbst melden, nur Rückfragen schreiben oder nur lesen.',
        `Die ${OPERATOR.name} betreibt Außencheck als technische Dienstleisterin des Vereins (im Folgenden „Betreiberin“). Den Vertrag über Außencheck hat sie mit Ihrem Verein geschlossen, nicht mit Ihnen.`,
      ],
    },
    {
      id: 'regeln',
      title: 'Regeln für die Nutzung',
      list: [
        'Melden Sie wahrheitsgemäß und so genau, wie es Ihnen möglich ist.',
        'Stellen Sie keine rechtswidrigen, beleidigenden oder herabwürdigenden Inhalte ein.',
        'Fotografieren und filmen Sie das Flugzeug, nicht Menschen: Vermeiden Sie erkennbare Personen auf Fotos und in Videos. Bei Videos wird der Ton mit aufgenommen.',
        'Laden Sie nur Fotos, Videos und Bilder hoch, die Sie selbst aufgenommen haben oder verwenden dürfen, und achten Sie die Rechte anderer.',
        'Versuchen Sie nicht, auf Daten anderer Vereine oder auf Funktionen zuzugreifen, die Ihre Rolle nicht vorsieht, und stören Sie den Betrieb nicht.',
        'Geben Sie Anmeldecodes an niemanden weiter – auch nicht an den Support.',
        'Bei Verstößen kann Ihr Verein Ihren Zugang beenden.',
      ],
    },
    {
      id: 'inhalte',
      title: 'Ihre Inhalte',
      paragraphs: [
        'Meldungen, Fotos, Videos, Rückfragen und Hinweise, die Sie erfassen, werden Teil der Dokumentation der Vereinsflugzeuge.',
        'Sie räumen dem Verein und – nur für den Betrieb von Außencheck – der Betreiberin das einfache, unentgeltliche und zeitlich unbegrenzte Recht ein, diese Inhalte zu speichern, anzuzeigen und zu verarbeiten, etwa Fotos zu verkleinern. Das Recht gilt auch über das Ende Ihrer Mitgliedschaft hinaus, weil die Dokumentation eines Flugzeugs vollständig bleiben muss.',
        `Verlassen Sie den Verein, bleiben Ihre Schadensmeldungen deshalb grundsätzlich erhalten; Ihr Name lässt sich dabei anonymisieren. Wenden Sie sich dazu an die Verwaltung Ihres Vereins oder an ${OPERATOR.email}.`,
      ],
    },
    {
      id: 'lufttuechtigkeit',
      title: 'Keine Bewertung der Lufttüchtigkeit',
      paragraphs: [
        'Außencheck dokumentiert Schäden, es bewertet sie nicht. Ob ein Flugzeug lufttüchtig ist, entscheiden weiterhin die Technik des Vereins und die verantwortliche Pilotin oder der verantwortliche Pilot. Außencheck ersetzt weder die Vorflugkontrolle noch eine Prüfung durch die Technik.',
        'Dass an einer Stelle kein Schaden eingetragen ist, heißt nicht, dass dort keiner ist.',
      ],
    },
    {
      id: 'verfuegbarkeit',
      title: 'Verfügbarkeit',
      paragraphs: [
        'Außencheck wird ohne Zusicherung einer bestimmten Verfügbarkeit bereitgestellt. Wartung und Störungen können die Nutzung zeitweise einschränken. In der App bleibt eine Meldung, die ohne Verbindung erfasst wurde, auf dem Gerät, bis sie übertragen ist.',
      ],
    },
    {
      id: 'haftung',
      title: 'Haftung',
      paragraphs: [
        'Die Nutzung ist für Sie kostenlos. Die Betreiberin haftet Ihnen gegenüber daher nur bei Vorsatz und grober Fahrlässigkeit, bei Verletzung des Lebens, des Körpers oder der Gesundheit sowie, soweit das Gesetz es zwingend vorschreibt, etwa nach dem Produkthaftungsgesetz.',
      ],
    },
    {
      id: 'aenderungen',
      title: 'Änderungen',
      paragraphs: [
        `Die Betreiberin kann diese Nutzungsbedingungen ändern, etwa wenn sich Funktionen ändern. Wesentliche Änderungen werden in der App angekündigt. Die jeweils gültige Fassung steht in der App und unter ${TERMS_URL}.`,
      ],
    },
    {
      id: 'kontakt',
      title: 'Kontakt und Datenschutz',
      paragraphs: [
        `Fragen zur Nutzung beantwortet zuerst die Verwaltung Ihres Vereins, danach ${SUPPORT_EMAIL}. Betreiberin ist die ${OPERATOR.name}, ${OPERATOR.street}, ${OPERATOR.city}; vollständige Angaben im Impressum.`,
        `Welche Daten verarbeitet werden, steht in der Datenschutzerklärung – in der App unter „Datenschutz“ und unter ${PRIVACY_URL}.`,
      ],
    },
  ],
};
